import { SubscriptionInterface } from '../helpers/objects/subscriptionObject';
import styles from '../css/ordersPage.module.scss';
import { freezeScroll, unfreezeScroll } from '../helpers/environment';
import { useEffect } from 'react';
import CustomTextInput from './customTextInput';
import { useTranslation } from 'react-i18next';

const SubscriptionDetailsModal = ({ subscription, isOpen, onClose }: { subscription: SubscriptionInterface; isOpen: boolean; onClose: () => void }) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isOpen) {
      freezeScroll();
    } else {
      unfreezeScroll();
    }
  }, [isOpen]);

  return (
    <div className={`${styles.orderDetailsModalContainer} ${isOpen ? styles.open : ''}`}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <img src="/assets/images/close-icon.svg" alt="Close" />
        </button>
        <h1>{t('subscriptionDetailsModal.orderDetails')}</h1>

        <hr />

        <h2>{t('promoCodeAddedModal.status')}</h2>

        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="status">{t('promoCodeAddedModal.status')}</label>
            <CustomTextInput type="text" id="status" disabled value={subscription?.status ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="createdAt">{t('promoCodeAddedModal.createdAt')}</label>
            <CustomTextInput
              type="text"
              id="createdAt"
              disabled
              value={
                subscription?.createdAt
                  ? new Date(subscription.createdAt).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })
                  : t('promoCodeAddedModal.notAvailable')
              }
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="validUntil">{t('promoCodeAddedModal.validUntil')}</label>
            <CustomTextInput
              type="text"
              id="validUntil"
              disabled
              value={
                subscription?.skycopAssignedInfo?.validTo?.date
                  ? new Date(subscription.skycopAssignedInfo?.validTo?.date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                    })
                  : t('promoCodeAddedModal.notAvailable')
              }
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="renewalDate">{t('promoCodeAddedModal.renewalDate')}</label>
            {subscription?.status !== t('promoCodeAddedModal.canceled') ? (
              <CustomTextInput
                type="text"
                id="renewalDate"
                disabled
                value={
                  subscription?.nextRenewalDate
                    ? new Date(subscription?.nextRenewalDate).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                      })
                    : t('promoCodeAddedModal.notAvailable')
                }
              />
            ) : (
              <CustomTextInput type="text" id="renewalDate" disabled value="--/--/----" />
            )}
          </div>
        </div>

        <hr />

        <h2>{t('promoCodeAddedModal.orderInformation')}</h2>

        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="firstName">{t('promoCodeAddedModal.firstName')}</label>
            <CustomTextInput type="text" id="firstName" disabled value={subscription?.user?.firstName ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="lastName">{t('promoCodeAddedModal.lastName')}</label>
            <CustomTextInput type="text" id="lastName" disabled value={subscription?.user?.lastName ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="email">{t('promoCodeAddedModal.email')}</label>
            <CustomTextInput type="email" id="email" disabled value={subscription?.user?.email ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="phoneNumber">{t('promoCodeAddedModal.phoneNumber')}</label>
            <CustomTextInput type="text" id="phoneNumber" disabled value={subscription?.user?.phoneNumber ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="country">{t('promoCodeAddedModal.country')}</label>
            <CustomTextInput type="text" id="country" disabled value={subscription?.user?.country ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
        </div>
        <hr />
        {subscription?.purchaseInformation?.otherPassengers?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <h2>{t('promoCodeAddedModal.additionalMembers')}</h2>
            {subscription?.purchaseInformation?.otherPassengers?.map((member, index) => (
              <div key={member.firstName + index} style={{ width: '100%' }}>
                <div className={styles.orderDetailsSection}>
                  <div className={styles.inputContainer}>
                    <label htmlFor="firstName">{t('promoCodeAddedModal.firstName')}</label>
                    <CustomTextInput type="text" id="firstName" disabled value={member?.firstName ?? t('promoCodeAddedModal.notAvailable')} />
                  </div>
                  <div className={styles.inputContainer}>
                    <label htmlFor="lastName">{t('promoCodeAddedModal.lastName')}</label>
                    <CustomTextInput type="text" id="lastName" disabled value={member?.lastName ?? t('promoCodeAddedModal.notAvailable')} />
                  </div>
                  <div className={styles.inputContainer}>
                    <label htmlFor="email">{t('promoCodeAddedModal.email')}</label>
                    <CustomTextInput type="email" id="email" disabled value={member?.email ?? t('promoCodeAddedModal.notAvailable')} />
                  </div>
                </div>
                <hr />
              </div>
            ))}
          </div>
        )}

        <h2>{t('promoCodeAddedModal.planDetails')}</h2>
        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="planName">{t('promoCodeAddedModal.planName')}</label>
            <CustomTextInput type="text" id="planName" disabled value={subscription?.plan?.product?.name ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="planPrice">{t('promoCodeAddedModal.planPrice')}</label>
            <CustomTextInput type="text" id="planPrice" disabled value={((subscription?.plan?.price ?? 0) / 100)?.toFixed(2) ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="familyPlan">{t('promoCodeAddedModal.familyPlan')}</label>
            <CustomTextInput type="text" id="familyPlan" disabled value={subscription?.plan?.product?.family?.toString() ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="perTicketPlan">{t('promoCodeAddedModal.perPlanTicket')}</label>
            <CustomTextInput type="text" id="perTicketPlan" disabled value={subscription?.plan?.product?.singleFlight?.toString() ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
        </div>

        <hr />

        <h2>{t('promoCodeAddedModal.coveredFlights')}</h2>
        {subscription?.coveredTrips?.length > 0 ? (
          subscription?.coveredTrips?.map((trip, index) => (
            <>
              <div className={styles.orderDetailsSection}>
                <div className={styles.inputContainer}>
                  <label htmlFor="departureAirport">{t('promoCodeAddedModal.departureAirport')}</label>
                  <CustomTextInput type="text" id="departureAirport" disabled value={trip?.departureAirport?.fullName && trip?.departureAirport?.IATA ? trip?.departureAirport?.fullName + ` (${trip?.departureAirport?.IATA})` : t('promoCodeAddedModal.notAvailable')} />
                </div>
                <div className={styles.inputContainer}>
                  <label htmlFor="arrivalAirport">{t('promoCodeAddedModal.arrivalAirport')}</label>
                  <CustomTextInput type="text" id="arrivalAirport" disabled value={trip?.arrivalAirport?.fullName && trip?.arrivalAirport?.IATA ? trip?.arrivalAirport?.fullName + ` (${trip?.arrivalAirport?.IATA})` : t('promoCodeAddedModal.notAvailable')} />
                </div>
                <div className={styles.inputContainer}>
                  <label htmlFor="airline">{t('promoCodeAddedModal.airline')}</label>
                  <CustomTextInput type="text" id="airline" disabled value={trip?.airline?.fullName && trip?.airline?.IATA ? trip?.airline?.fullName + ` (${trip?.airline?.IATA})` : t('promoCodeAddedModal.notAvailable')} />
                </div>
                <div className={styles.inputContainer}>
                  <label htmlFor="flightNumber">{t('promoCodeAddedModal.flightNumber')}</label>
                  <CustomTextInput type="text" id="flightNumber" disabled value={trip?.flightNumber ?? t('promoCodeAddedModal.notAvailable')} />
                </div>
                <div className={styles.inputContainer}>
                  <label htmlFor="flightDate">{t('promoCodeAddedModal.flightDate')}</label>
                  <CustomTextInput
                    type="text"
                    id="flightDate"
                    disabled
                    value={
                      new Date(trip?.flightDate).toLocaleString('en-US', {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                      }) ?? t('promoCodeAddedModal.notAvailable')
                    }
                  />
                </div>
              </div>
              <hr />
            </>
          ))
        ) : (
          <p>{t('promoCodeAddedModal.noCoveredTripsFound')}</p>
        )}

        <h2>{t('promoCodeAddedModal.skycopInfo')}</h2>
        <div className={styles.orderDetailsSection}>
          <div className={styles.inputContainer}>
            <label htmlFor="code">{t('promoCodeAddedModal.code')}</label>
            <CustomTextInput type="text" id="code" disabled value={subscription?.skycopAssignedInfo?.code ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="expiringDate">{t('promoCodeAddedModal.expiringDate')}</label>
            <CustomTextInput
              type="text"
              id="expiringDate"
              disabled
              value={
                subscription?.skycopAssignedInfo?.expiringDate?.date
                  ? new Date(subscription?.skycopAssignedInfo?.expiringDate?.date).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'numeric',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })
                  : t('promoCodeAddedModal.notAvailable')
              }
            />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="skycopId">{t('promoCodeAddedModal.id')}</label>
            <CustomTextInput type="text" id="skycopID" disabled value={subscription?.skycopAssignedInfo?.id ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
          <div className={styles.inputContainer}>
            <label htmlFor="skycopType">{t('promoCodeAddedModal.type')}</label>
            <CustomTextInput type="text" id="skycopType" disabled value={subscription?.skycopAssignedInfo?.type ?? t('promoCodeAddedModal.notAvailable')} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionDetailsModal;
